/* eslint-disable no-console */

const logLevel = process.env.NODE_ENV === 'production' ? 1 : 8

const logLevels = {
  fatal: 0,
  error: 0,
  warn: 1,
  log: 2,
  info: 3,
  success: 3,
  graphql: 4,
  subscriptions: 4,
  debug: 4,
  trace: 5,
  silent: Infinity,
}

const logColors: Partial<Record<Level, string>> = {
  fatal: '#c0392b',
  error: '#c0392b',
  warn: '#f39c12',
  info: '#00BCD4',
  success: '#2ecc71',
  graphql: '#9600cc',
  subscriptions: '#2ecc71',
}

const defaultLogColor = '#7f8c8d'

type Level = keyof typeof logLevels
type ConsoleMethod = keyof Console
type Logger = { [K in Level]: (...args: any[]) => void }

const devNull = () => {}

export const logger = (Object.keys(logLevels) as Level[]).reduce(
  (acc, type) => {
    if (logLevels[type] >= logLevel) {
      acc[type] = devNull
      return acc
    }

    let method: ConsoleMethod = 'log'
    if (logLevels[type] === 0) {
      method = 'error'
    }
    if (logLevels[type] === 1) {
      method = 'warn'
    }
    if (logLevels[type] === 4) {
      method = 'debug'
    }

    acc[type] = (...args: any[]) => {
      if (process.env.NODE_ENV === 'production' && method === 'error') {
        // This prevents having the badge in error logging third party
        console[`__${method}` as ConsoleMethod](...args)
        return
      }

      const color = logColors[type] || defaultLogColor
      const style = `
          background: ${color};
          border-radius: 0.5em;
          color: white;
          font-weight: bold;
          padding: 2px 0.5em;
        `
      const badge = `%c${type}`

      if (typeof args[0] === 'string') {
        console[`__${method}` as ConsoleMethod](
          `${badge}%c ${args[0]}`,
          style,
          '',
          ...args.slice(1)
        )
      } else {
        console[`__${method}` as ConsoleMethod](badge, style, ...args)
      }
    }

    return acc
  },
  {} as Logger
)

const wrapConsole = () => {
  ;(Object.keys(logLevels) as Level[]).forEach(key => {
    console[`__${key}` as ConsoleMethod] = console[key as ConsoleMethod]
    console[key as ConsoleMethod] = logger[key]
  })
}

wrapConsole()
